import React, { useState } from 'react';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useI18n } from '../i18n/i18nContext';
import { formatInTimeZone } from 'date-fns-tz';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { ConfirmDialog } from './ConfirmDialog';
import { useServiceMetaStore } from '../stores/serviceMetaStore';

const TALLINN_TIMEZONE = 'Europe/Tallinn';

interface BookingCartProps {
  hideCheckoutButton?: boolean;
}

export const BookingCart: React.FC<BookingCartProps> = ({ hideCheckoutButton = false }) => {
  const { items, removeBooking, updateBooking } = useBookingCartStore();
  const { credits: serviceCredits, getMeta } = useServiceMetaStore();
  const navigate = useLocalizedNavigate();
  const { t, language } = useI18n();
  const dateLocale = language === 'ru' ? ruLocale : language === 'et' ? etLocale : language === 'fi' ? fiLocale : undefined;
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const handleRemove = (cartItemId: string) => {
    setConfirmRemove(cartItemId);
  };

  const confirmRemoveBooking = () => {
    if (confirmRemove) {
      removeBooking(confirmRemove);
      setConfirmRemove(null);
    }
  };

  const handleChange = (item: any) => {
    const startTime = new Date(item.startTime);
    const preselectedTime = formatInTimeZone(startTime, TALLINN_TIMEZONE, 'HH:mm');
    const navigationState = {
      preselectedDate: startTime,
      preselectedTime: preselectedTime,
      isChanging: true,
      cartItemId: item.cartItemId
    };
    navigate(`/book/${item.serviceId}`, {
      state: navigationState
    });
  };

  const getTitle = () => {
    const count = items.length;
    return count === 1 
      ? t.cart.bookingSelected?.replace('{{count}}', count.toString()) || `${count} Booking Selected`
      : t.cart.bookingsSelected?.replace('{{count}}', count.toString()) || `${count} Bookings Selected`;
  };

  if (items.length === 0) {
    return null;
  }

  const sortedItems = [...items].sort((a, b) => {
    return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  });

  return (
    <>
      {confirmRemove && (
        <ConfirmDialog
          title={t.cart.removeBookingTitle || 'Remove Booking'}
          message={t.cart.confirmRemove || 'Are you sure you want to remove this booking?'}
          confirmText={t.cart.remove || 'Remove'}
          cancelText={t.common.cancel || 'Cancel'}
          onConfirm={confirmRemoveBooking}
          onCancel={() => setConfirmRemove(null)}
          type="danger"
        />
      )}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{getTitle()}</h2>
      
      <div className="space-y-4 mb-4">
        {sortedItems.map((item) => {
          const meta = getMeta(item.serviceId);
          // Default to 1 to prevent errors if meta is not found or is zero.
          const maxParticipants = meta?.maxParticipants || 1;

          return (
            <div
              key={item.cartItemId}
              className="border-b border-gray-200 pb-4"
            >
              <div
                onClick={() => handleChange(item)}
                className="flex justify-between items-start cursor-pointer hover:bg-gray-50 p-3 rounded-md transition-colors"
              >
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    {item.serviceName}
                    {serviceCredits[item.serviceId] > 1 && (
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({serviceCredits[item.serviceId]} {t.cart.credits})
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600">
                    {formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'EEE, MMM d', { locale: dateLocale })} {t.cart.at} {formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'HH:mm')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {item.duration} {t.booking.minutes}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChange(item);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    {t.cart.change || 'Change'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item.cartItemId);
                    }}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    {t.cart.remove}
                  </button>
                </div>
              </div>

              {/* Render quantity controls only if the service supports more than one participant */}
              {maxParticipants > 1 && (
                <div className="flex items-center space-x-3 mt-2">
                  <span className="text-sm font-medium text-gray-700">{t.cart.participants}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newQuantity = Math.max(1, item.quantity - 1);
                      // CRITICAL: When decreasing quantity, we must also shorten the invitees
                      // array to prevent holding onto emails for participants who no longer exist.
                      const newInvitees = item.invitees.slice(0, newQuantity - 1);
                      updateBooking(item.cartItemId, { quantity: newQuantity, invitees: newInvitees });
                    }}
                    disabled={item.quantity <= 1}
                    className="px-2 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    -
                  </button>
                  <span className="font-bold text-lg">{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newQuantity = Math.min(maxParticipants, item.quantity + 1);
                      updateBooking(item.cartItemId, { quantity: newQuantity });
                    }}
                    disabled={item.quantity >= maxParticipants}
                    className="px-2 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    +
                  </button>
                </div>
              )}

              {/* Render email inputs if quantity is greater than 1 */}
              {item.quantity > 1 && (
                <div className="mt-3 pl-2 border-l-2 border-gray-200 space-y-2">
                  <p className="text-sm font-semibold text-gray-600">{t.cart.participantEmails}</p>
                  {/* Create a temporary array of the correct length to map over and render inputs */}
                  {Array.from({ length: item.quantity - 1 }).map((_, index) => (
                    <input
                      key={index}
                      type="email"
                      placeholder={t.cart.participantEmailPlaceholder.replace('{{count}}', (index + 2).toString())}
                      className="w-full p-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 transition"
                      value={item.invitees[index] || ''}
                      onChange={(e) => {
                        // IMPORTANT: Create a new array to avoid direct state mutation.
                        const newInvitees = [...item.invitees];
                        newInvitees[index] = e.target.value;
                        updateBooking(item.cartItemId, { invitees: newInvitees });
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!hideCheckoutButton && (
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/checkout')}
            className="w-full sm:w-auto px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium flex items-center justify-center gap-2"
          >
            <span>✓</span>
            {t.cart.proceedToCheckout}
          </button>
        </div>
      )}
    </div>
    </>
  );
};
